import React, { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  RefreshCw,
  MapPin,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  getTaxGrouped,
  createCodes,
  updateCodes,
  deleteCodes,
  createTax,
  updateTax,
  deleteTax,
} from "../../services/api";
import {
  StateSelect,
  CitySelect,
} from "react-country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";
import { toast } from "react-hot-toast";

const COUNTRIES = [
  { id: 233, code: "US", name: "United States", flag: "🇺🇸" },
  { id: 39,  code: "CA", name: "Canada",        flag: "🇨🇦" },
];

interface TaxRule {
  _id: string;
  country: string;
  state?: string;
  city?: string;
  category_code: string;
  // US field
  tax_percent?: number;
  // Canada fields
  gst?: number;
  pst?: number;
  is_taxable: boolean;
  isActive: boolean;
}

interface TaxCode {
  _id: string;
  code: string;
  name: string;
  description: string;
  taxable: boolean;
  taxes: TaxRule[];
  taxCount: number;
}

const TaxManagement: React.FC = () => {
  // Global country selector
  const [globalCountry, setGlobalCountry] = useState(COUNTRIES[0]);

  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [expandedCodes, setExpandedCodes] = useState<Set<string>>(new Set());

  // Tax Code Modal State
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [editingCode, setEditingCode] = useState<TaxCode | null>(null);
  const [codeFormData, setCodeFormData] = useState({
    code: "",
    name: "",
    description: "",
    taxable: true,
  });
  const [codeFormLoading, setCodeFormLoading] = useState(false);

  // Tax Rule Modal State
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [editingTax, setEditingTax] = useState<TaxRule | null>(null);
  const [selectedCodeForTax, setSelectedCodeForTax] = useState<string>("");
  const [stateid, setstateid] = useState(0);
  const [taxFormData, setTaxFormData] = useState({
    state: "",
    city: "",
    // US
    tax_percent: "",
    // CA
    gst: "",
    pst: "",
  });
  const [taxable, setTaxable] = useState<boolean>(true);
  const [active, setActive] = useState<boolean>(true);

  const isCanada = globalCountry.code === "CA";

  useEffect(() => {
    fetchTaxCodes();
    // reset pagination when country changes
    setCurrentPage(1);
    setExpandedCodes(new Set());
  }, [globalCountry]);

  const fetchTaxCodes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getTaxGrouped(globalCountry.code);
      if (response.data.success && Array.isArray(response.data.data)) {
        setTaxCodes(response.data.data);
      } else {
        setTaxCodes([]);
        setError("Invalid data format received from server");
      }
    } catch {
      setError("Failed to load tax data. Please try again.");
      setTaxCodes([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (codeId: string) => {
    setExpandedCodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(codeId)) {
        newSet.delete(codeId);
      } else {
        newSet.add(codeId);
      }
      return newSet;
    });
  };

  // ============== TAX CODE CRUD ==============
  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCodeFormLoading(true);
    setError(null);

    const data = {
      code: codeFormData.code.toUpperCase().replace(/[^A-Z_]/g, ""),
      name: codeFormData.name,
      description: codeFormData.description,
      taxable: codeFormData.taxable,
    };

    try {
      if (editingCode) {
        await updateCodes(editingCode._id, data);
        toast.success("Tax code updated successfully!");
      } else {
        await createCodes(data);
        toast.success("Tax code added successfully!");
      }

      resetCodeForm();
      setShowCodeModal(false);
      fetchTaxCodes();
    } catch (err) {
      setError("Failed to save tax code.");
      toast.error("Failed to save tax code.");
    } finally {
      setCodeFormLoading(false);
    }
  };

  const handleEditCode = (code: TaxCode) => {
    setEditingCode(code);
    setCodeFormData({
      code: code.code,
      name: code.name,
      description: code.description || "",
      taxable: code.taxable !== undefined ? code.taxable : true,
    });
    setShowCodeModal(true);
  };

  const handleDeleteCode = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this tax code? This will also remove all associated tax rules.")) return;

    try {
      await deleteCodes(id);
      fetchTaxCodes();
      toast.success("Tax code deleted successfully!");
    } catch (err) {
      setError("Failed to delete tax code.");
      toast.error("Failed to delete tax code.");
    }
  };

  const resetCodeForm = () => {
    setEditingCode(null);
    setCodeFormData({
      code: "",
      name: "",
      description: "",
      taxable: true,
    });
  };

  const openAddCodeModal = () => {
    resetCodeForm();
    setShowCodeModal(true);
  };

  // ============== TAX RULE CRUD ==============
  const handleTaxSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const taxData: any = {
      country: globalCountry.code,
      state: taxFormData.state ? taxFormData.state.toUpperCase() : null,
      city: taxFormData.city ? taxFormData.city.toUpperCase() : null,
      category_code: selectedCodeForTax.toUpperCase(),
      is_taxable: taxable,
      isActive: active,
    };

    if (isCanada) {
      taxData.gst = parseFloat(taxFormData.gst) || 0;
      taxData.pst = parseFloat(taxFormData.pst) || 0;
    } else {
      taxData.tax_percent = parseFloat(taxFormData.tax_percent);
    }

    try {
      setError(null);
      if (editingTax) {
        await updateTax(globalCountry.code, editingTax._id, taxData);
        toast.success("Tax rule updated successfully!");
      } else {
        await createTax(globalCountry.code, taxData);
        toast.success("Tax rule added successfully!");
      }
      await fetchTaxCodes();
      resetTaxForm();
      setShowTaxModal(false);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Failed to save tax rule. Please try again."
      );
      toast.error("Failed to save tax rule.");
    }
  };

  const handleEditTax = (tax: TaxRule, categoryCode: string) => {
    setEditingTax(tax);
    setSelectedCodeForTax(categoryCode);
    setTaxFormData({
      state: tax.state || "",
      city: tax.city || "",
      tax_percent: tax.tax_percent != null ? tax.tax_percent.toString() : "",
      gst: tax.gst != null ? tax.gst.toString() : "",
      pst: tax.pst != null ? tax.pst.toString() : "",
    });
    setTaxable(tax.is_taxable);
    setActive(tax.isActive);
    setShowTaxModal(true);
  };

  const handleDeleteTax = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this tax rule?")) {
      try {
        setError(null);
        await deleteTax(globalCountry.code, id);
        await fetchTaxCodes();
        toast.success("Tax rule deleted successfully!");
      } catch {
        setError("Failed to delete tax rule. Please try again.");
        toast.error("Failed to delete tax rule.");
      }
    }
  };

  const resetTaxForm = () => {
    setEditingTax(null);
    setSelectedCodeForTax("");
    setTaxFormData({
      state: "",
      city: "",
      tax_percent: "",
      gst: "",
      pst: "",
    });
    setTaxable(true);
    setActive(true);
    setstateid(0);
  };

  const openAddTaxModal = (categoryCode: string) => {
    resetTaxForm();
    setSelectedCodeForTax(categoryCode);
    setShowTaxModal(true);
  };

  // Filter and pagination logic
  const filteredTaxCodes = taxCodes.filter(
    (code) =>
      code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      code.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (code.description && code.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.ceil(filteredTaxCodes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTaxCodes = filteredTaxCodes.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="relative max-w-7xl mx-auto pt-0 p-4 lg:p-8 flex flex-col overflow-y-auto no-scrollbar h-screen">
      {/* ================= HEADER ================= */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-800">Tax Management</h1>
            {/* Global Country Dropdown */}
            <div className="flex items-center gap-1.5">
              {COUNTRIES.map((c) => (
                <button
                  key={c.code}
                  onClick={() => setGlobalCountry(c)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                    globalCountry.code === c.code
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:border-blue-400"
                  }`}
                >
                  <span>{c.flag}</span>
                  <span>{c.code}</span>
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={openAddCodeModal}
            className="w-auto self-start sm:self-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition inline-flex items-center gap-2 whitespace-nowrap"
          >
            <Plus size={18} />
            Add Tax Code
          </button>
        </div>

        {/* ================= SEARCH ================= */}
        <div className="rounded-lg mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Search Tax Codes
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by code, name or description..."
                className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={fetchTaxCodes}
              className="h-10 w-10 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex items-center justify-center"
              title="Refresh"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      {/* ================= ERROR ================= */}
      {error && (
        <div className="bg-red-500 text-white p-3 rounded-md mb-4 text-center">
          {error}
        </div>
      )}

      {/* ================= CONTENT ================= */}
      <div className="flex-1">
        {paginatedTaxCodes.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <p className="text-lg font-medium">No tax codes found</p>
              <p className="text-sm mt-1">
                {searchTerm
                  ? "Try searching with a different term"
                  : "Add your first tax code to get started"}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {paginatedTaxCodes.map((code) => (
              <div
                key={code._id}
                className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Tax Code Header */}
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => toggleExpand(code._id)}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="self-center flex items-center text-gray-500">
                        {expandedCodes.has(code._id) ? (
                          <ChevronDown size={20} />
                        ) : (
                          <ChevronRight size={20} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          <h3 className="text-xl font-bold text-gray-800 m-0">
                            {code.code}
                          </h3>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              code.taxable
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {code.taxable ? "Taxable" : "Non-Taxable"}
                          </span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {code.taxCount} tax rule{code.taxCount !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <p className="text-gray-700 font-medium">{code.name}</p>
                        {code.description && (
                          <p className="text-gray-500 text-sm mt-1 line-clamp-1">
                            {code.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Tax Code Actions */}
                    <div
                      className="grid w-full grid-cols-2 gap-2 sm:w-auto sm:flex"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => handleEditCode(code)}
                        className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded text-xs sm:text-sm hover:bg-blue-700 transition"
                      >
                        <Edit size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteCode(code._id)}
                        className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-red-600 text-white rounded text-xs sm:text-sm hover:bg-red-700 transition"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Tax Rules Section */}
                {expandedCodes.has(code._id) && (
                  <div className="border-t bg-gray-50 p-3 sm:p-4">
                    <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <h4 className="font-semibold text-gray-700">Tax Rules</h4>
                      <button
                        onClick={() => openAddTaxModal(code.code)}
                        className="w-full sm:w-auto px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition flex items-center justify-center gap-1"
                      >
                        <Plus size={14} />
                        Add Tax Rule
                      </button>
                    </div>

                    {code.taxes.length === 0 ? (
                      <p className="text-gray-500 text-sm italic">
                        No tax rules defined for this code. Click "Add Tax Rule" to create one.
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {code.taxes.map((tax) => (
                          <div
                            key={tax._id}
                            className="bg-white border rounded-lg p-3 shadow-sm"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                {isCanada ? (
                                  <div>
                                    <p className="text-lg font-bold text-gray-800">
                                      GST: {tax.gst ?? 0}%
                                    </p>
                                    <p className="text-lg font-bold text-gray-800">
                                      PST: {tax.pst ?? 0}%
                                    </p>
                                  </div>
                                ) : (
                                  <p className="text-2xl font-bold text-gray-800">
                                    {tax.tax_percent}%
                                  </p>
                                )}
                                <div className="flex items-center gap-1 text-gray-600 text-sm mt-1">
                                  <MapPin size={12} />
                                  <span className="break-words">
                                    {tax.country}
                                    {tax.state && `, ${tax.state}`}
                                    {tax.city && `, ${tax.city}`}
                                  </span>
                                </div>
                              </div>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  tax.is_taxable
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {tax.is_taxable ? "Taxable" : "Non-Taxable"}
                              </span>
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-2">
                              <button
                                onClick={() => handleEditTax(tax, code.code)}
                                className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition"
                              >
                                <Edit size={12} />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteTax(tax._id)}
                                className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition"
                              >
                                <Trash2 size={12} />
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= PAGINATION ================= */}
      {filteredTaxCodes.length > 0 && totalPages > 1 && (
        <div className="py-4 flex justify-center">
          <div className="flex gap-2 items-center">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Prev
            </button>

            {(() => {
              const pages = [];
              const showEllipsis = totalPages > 7;

              if (!showEllipsis) {
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === i
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {i}
                    </button>
                  );
                }
              } else {
                const startPage = Math.max(1, currentPage - 2);
                const endPage = Math.min(totalPages, currentPage + 2);

                if (startPage > 1) {
                  pages.push(
                    <button
                      key={1}
                      onClick={() => setCurrentPage(1)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === 1
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      1
                    </button>
                  );

                  if (startPage > 2) {
                    pages.push(
                      <span key="ellipsis1" className="px-2 py-1 text-gray-500">
                        ...
                      </span>
                    );
                  }
                }

                for (let i = startPage; i <= endPage; i++) {
                  if (i !== 1 && i !== totalPages) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1 rounded transition ${
                          currentPage === i
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 hover:bg-gray-300"
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                }

                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <span key="ellipsis2" className="px-2 py-1 text-gray-500">
                        ...
                      </span>
                    );
                  }

                  pages.push(
                    <button
                      key={totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === totalPages
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {totalPages}
                    </button>
                  );
                }
              }

              return pages;
            })()}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ================= TAX CODE MODAL ================= */}
      {showCodeModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ top: "0", left: "0", right: "0", bottom: "0" }}
        >
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowCodeModal(false)}
          />

          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col mx-auto">
            <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800 mb-0">
                {editingCode ? "Edit Tax Code" : "Add New Tax Code"}
              </h2>
              <button
                type="button"
                onClick={() => setShowCodeModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <form id="code-form" onSubmit={handleCodeSubmit} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Code *
                    </label>
                    <input
                      type="text"
                      value={codeFormData.code}
                      onChange={(e) =>
                        setCodeFormData({
                          ...codeFormData,
                          code: e.target.value.toUpperCase().replace(/[^A-Z_]/g, ""),
                        })
                      }
                      placeholder="Enter code (uppercase letters and underscores only)"
                      required
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={codeFormData.name}
                      onChange={(e) =>
                        setCodeFormData({ ...codeFormData, name: e.target.value })
                      }
                      placeholder="Enter code name"
                      required
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Description *
                    </label>
                    <textarea
                      value={codeFormData.description}
                      onChange={(e) =>
                        setCodeFormData({
                          ...codeFormData,
                          description: e.target.value,
                        })
                      }
                      placeholder="Enter code description"
                      required
                      rows={3}
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                    />
                  </div>

                  <div>
                    <label className="block mb-3 text-sm font-medium text-gray-700">
                      Tax Status *
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="code-taxable"
                          value="true"
                          checked={codeFormData.taxable === true}
                          onChange={() =>
                            setCodeFormData({ ...codeFormData, taxable: true })
                          }
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Taxable</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="code-taxable"
                          value="false"
                          checked={codeFormData.taxable === false}
                          onChange={() =>
                            setCodeFormData({ ...codeFormData, taxable: false })
                          }
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Non-Taxable</span>
                      </label>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-lg flex justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowCodeModal(false)}
                className="px-3 py-2 sm:px-5 sm:py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="code-form"
                className="px-3 py-2 sm:px-5 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                disabled={codeFormLoading}
              >
                {codeFormLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">⏳</span>
                    Saving...
                  </span>
                ) : editingCode ? (
                  "Update Code"
                ) : (
                  "Add Code"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= TAX RULE MODAL ================= */}
      {showTaxModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ top: "0", left: "0", right: "0", bottom: "0" }}
        >
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowTaxModal(false)}
          />

          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col mx-auto">
            <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800">
                {editingTax ? "Edit Tax Rule" : "Add New Tax Rule"}
                <span className="ml-2 text-sm font-normal text-gray-500">
                  {globalCountry.flag} {globalCountry.name}
                </span>
              </h2>
              <button
                type="button"
                onClick={() => setShowTaxModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <form id="tax-form" onSubmit={handleTaxSubmit} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Tax Code
                    </label>
                    <div className="w-full p-2.5 rounded-md border border-gray-300 bg-gray-100">
                      <span className="font-semibold">{selectedCodeForTax}</span>
                    </div>
                  </div>

                  {/* Canada: GST + PST fields */}
                  {isCanada ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                          GST (%) *
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={taxFormData.gst}
                            onChange={(e) =>
                              setTaxFormData({ ...taxFormData, gst: e.target.value.replace(/[^0-9.]/g, "") })
                            }
                            placeholder="0.00"
                            required
                            className="w-full p-2.5 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          />
                          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                        </div>
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                          PST (%)
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={taxFormData.pst}
                            onChange={(e) =>
                              setTaxFormData({ ...taxFormData, pst: e.target.value.replace(/[^0-9.]/g, "") })
                            }
                            placeholder="0.00"
                            className="w-full p-2.5 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          />
                          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* US: single tax_percent field */
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Tax Percentage *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={taxFormData.tax_percent}
                          onChange={(e) =>
                            setTaxFormData({ ...taxFormData, tax_percent: e.target.value.replace(/[^0-9.]/g, "") })
                          }
                          placeholder="0.00"
                          required
                          className="w-full p-2.5 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      {isCanada ? "Province" : "State"}
                    </label>
                    <StateSelect
                      countryid={globalCountry.id}
                      containerClassName="w-full"
                      inputClassName="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      onChange={(e: any) => {
                        setstateid(e.id);
                        setTaxFormData({
                          ...taxFormData,
                          state: e.state_code,
                          city: "",
                        });
                      }}
                      placeHolder={`Select ${isCanada ? "Province" : "State"} (optional)`}
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      City
                    </label>
                    <CitySelect
                      countryid={globalCountry.id}
                      stateid={stateid}
                      containerClassName="w-full"
                      inputClassName="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      onChange={(e: any) => {
                        setTaxFormData({ ...taxFormData, city: e.name });
                      }}
                      placeHolder="Select City (optional)"
                    />
                  </div>

                  <div>
                    <label className="block mb-3 text-sm font-medium text-gray-700">
                      Tax Status *
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="tax-taxable"
                          value="true"
                          checked={taxable === true}
                          onChange={() => setTaxable(true)}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Taxable</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="tax-taxable"
                          value="false"
                          checked={taxable === false}
                          onChange={() => setTaxable(false)}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Non-Taxable</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block mb-3 text-sm font-medium text-gray-700">
                      Rule Status *
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="tax-active"
                          value="true"
                          checked={active === true}
                          onChange={() => setActive(true)}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Active</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="tax-active"
                          value="false"
                          checked={active === false}
                          onChange={() => setActive(false)}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Inactive</span>
                      </label>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-lg flex justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowTaxModal(false)}
                className="px-3 py-2 sm:px-5 sm:py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="tax-form"
                className="px-3 py-2 sm:px-5 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
              >
                {editingTax ? "Update Tax Rule" : "Add Tax Rule"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxManagement;
